///
/// EarthBrowser
///
/// Created by Matt Giger on 12/13/2024
/// Copyright (c) 2024 Geodata Labs
///

import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { useToast } from "@/components/ui/use-toast"
import { useEntitlements } from "@/hooks/useEntitlements"
import { usePushChannel } from "@/hooks/usePushChannel"
import { setProfileOpen, setSubscribeOpen } from "@/redux/dialogSlice"
import { useDeletePlacemarkAlertMutation, useListPlacemarkAlertsQuery, usePutPlacemarkAlertMutation } from "@/redux/placemarkAlertsApi"
import { useListPushSubscriptionsQuery } from "@/redux/pushSubscriptionsApi"
import { useAppDispatch, useAppSelector } from "@/redux/store"
import { useGetNotificationSettingsQuery, useUpdateNotificationSettingsMutation } from "@/redux/userApi"
import { currentPushEndpoint, pushPermission, pushSupported } from "@/utils/webPush"
import type { AlertRuleSeverity, PlacemarkAlertRuleRecord } from "@shared-types"
import type { NotificationSettings } from "@shared-types/user"
import { BellRing, Loader2, MapPin, Trash2 } from "lucide-react"
import { useEffect, useState } from "react"

interface NotificationRow {
	id: keyof NotificationSettings
	title: string
	description: string
}

const notificationRows: NotificationRow[] = [
	{
		id: "security_alerts",
		title: "Security Alerts",
		description: "Sign-ins from new devices and important account changes",
	},
	{
		id: "product_updates",
		title: "Product Updates",
		description: "New features, layers and improvements to EarthBrowser",
	},
	{
		id: "newsletter",
		title: "Newsletter",
		description: "Occasional stories and highlights from the EarthBrowser community",
	},
]

export function NotificationsSection() {
	const { data: settings, isLoading } = useGetNotificationSettingsQuery()
	const [updateSettings] = useUpdateNotificationSettingsMutation()
	const { toast } = useToast()

	async function handleToggle(id: keyof NotificationSettings, checked: boolean) {
		try {
			await updateSettings({ [id]: checked }).unwrap()
		} catch (error) {
			console.error("Failed to update notification settings:", error)
			toast({
				title: "Update Failed",
				variant: "destructive",
				description: "Unable to save your notification preferences. Please try again.",
			})
		}
	}

	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-4">
				<Loader2 className="h-5 w-5 animate-spin text-teal-500" />
			</div>
		)
	}

	return (
		<div className="space-y-2">
			{notificationRows.map((row) => (
				<div key={row.id} className="rounded-lg border border-border bg-background p-3 transition-colors hover:bg-muted/50">
					<div className="flex items-center justify-between gap-3">
						<div className="flex-1">
							<Label htmlFor={`notify-${row.id}`} className="flex flex-col gap-0.5 cursor-pointer">
								<span className="text-sm font-semibold">{row.title}</span>
								<span className="text-xs text-muted-foreground font-normal leading-snug">
									{row.description}
								</span>
							</Label>
						</div>
						<Switch
							id={`notify-${row.id}`}
							checked={settings?.[row.id] ?? false}
							onCheckedChange={(checked) => handleToggle(row.id, checked)}
						/>
					</div>
				</div>
			))}
			<p className="text-xs text-muted-foreground px-1 pt-1">
				Email notifications are sent to your account email address.
			</p>

			<LocationAlertsSection />
		</div>
	)
}

const cSeverityOptions: Array<{ value: AlertRuleSeverity; label: string }> = [
	{ value: "Extreme", label: "Extreme only" },
	{ value: "Severe", label: "Severe and up" },
	{ value: "Moderate", label: "Moderate and up" },
	{ value: "Minor", label: "All severities" },
]

/**
 * Location-based alerts (subscription feature): rules watching the user's
 * placemarks for active severe weather. Rules are created from the
 * placemark's info panel on the globe; here they can be tuned (severity
 * floor), paused, or removed.
 */
function LocationAlertsSection() {
	const dispatch = useAppDispatch()
	const { isPaid } = useEntitlements()
	const { data, isLoading } = useListPlacemarkAlertsQuery(undefined, { skip: !isPaid })

	return (
		<div className="pt-4 space-y-2">
			<div>
				<h3 className="text-sm font-semibold">Location Alerts</h3>
				<p className="text-xs text-muted-foreground leading-snug">
					Get notified when any of your saved places is under an active weather alert,
					like a Red Flag Warning or Severe Thunderstorm Warning.
				</p>
			</div>

			{!isPaid ? (
				<div className="rounded-lg border border-border bg-muted/30 p-3 flex items-center justify-between gap-3">
					<p className="text-xs text-muted-foreground leading-snug flex-1">
						Location alerts are a subscriber feature.
					</p>
					<Button
						size="sm"
						className="h-7 text-xs shrink-0"
						onClick={() => {
							dispatch(setProfileOpen(false))
							dispatch(setSubscribeOpen(true))
						}}
					>
						Subscribe
					</Button>
				</div>
			) : (
				<AlertDeliverySettings hasRules={(data?.rules.length ?? 0) > 0} />
			)}

			{!isPaid ? null : isLoading ? (
				<div className="flex items-center justify-center py-3">
					<Loader2 className="h-4 w-4 animate-spin text-teal-500" />
				</div>
			) : !data || data.rules.length === 0 ? (
				<p className="text-xs text-muted-foreground rounded-lg border border-dashed border-border p-3">
					No location alerts yet. Open one of your places on the globe and choose
					&ldquo;Notify me of weather alerts here&rdquo;.
				</p>
			) : (
				<div className="space-y-2">
					{data.rules.map(rule => (
						<LocationAlertRow key={rule.placemark_id} rule={rule} />
					))}
					<p className="text-[11px] text-muted-foreground px-1">
						{data.rules.length} of {data.limits.max_rules} alert rules · checked hourly
					</p>
				</div>
			)}
		</div>
	)
}

/**
 * Delivery channels for location alerts. Push (this browser) is the
 * primary channel; email is a separate opt-in (default off — alert
 * volume must never hurt email sender reputation). Warns when rules
 * exist but nothing can deliver.
 */
function AlertDeliverySettings({ hasRules }: { hasRules: boolean }) {
	const { data: settings } = useGetNotificationSettingsQuery()
	const [updateSettings] = useUpdateNotificationSettingsMutation()
	const { data: pushData } = useListPushSubscriptionsQuery()
	const { enablePush, disablePush } = usePushChannel()
	const { toast } = useToast()

	const [thisEndpoint, setThisEndpoint] = useState<string | null>(null)
	const [busy, setBusy] = useState(false)
	useEffect(() => {
		void currentPushEndpoint().then(setThisEndpoint)
	}, [])

	const supported = pushSupported()
	const permission = pushPermission()
	const thisBrowserOn = thisEndpoint !== null &&
		(pushData?.subscriptions.some(s => s.endpoint === thisEndpoint) ?? false)
	const emailOn = settings?.location_alert_email ?? false
	const noChannel = hasRules && !thisBrowserOn && !emailOn &&
		(pushData?.subscriptions.length ?? 0) === 0

	const handlePushToggle = async (checked: boolean) => {
		if (busy) return
		setBusy(true)
		try {
			if (checked) {
				const status = await enablePush()
				if (status === "denied") {
					toast({
						title: "Notifications blocked",
						description: "Allow notifications for this site in your browser settings, then try again.",
						variant: "destructive",
					})
				} else if (status === "subscribed") {
					setThisEndpoint(await currentPushEndpoint())
				}
			} else {
				await disablePush()
				setThisEndpoint(null)
			}
		} finally {
			setBusy(false)
		}
	}

	const handleEmailToggle = async (checked: boolean) => {
		try {
			await updateSettings({ location_alert_email: checked }).unwrap()
		} catch (error) {
			console.error("Failed to update alert email setting:", error)
			toast({
				title: "Update Failed",
				variant: "destructive",
				description: "Unable to save this setting. Please try again.",
			})
		}
	}

	return (
		<div className="rounded-lg border border-border bg-background p-3 space-y-3">
			<div className="flex items-center justify-between gap-3">
				<Label htmlFor="alert-push" className="flex flex-col gap-0.5 cursor-pointer flex-1">
					<span className="text-sm font-semibold flex items-center gap-1.5">
						<BellRing className="h-3.5 w-3.5 text-teal-500" />
						Notifications in this browser
					</span>
					<span className="text-xs text-muted-foreground font-normal leading-snug">
						{!supported
							? "Not supported here — on iPhone/iPad, add EarthBrowser to your Home Screen first"
							: permission === "denied"
								? "Blocked in your browser settings for this site"
								: "Instant alerts on this device, even with EarthBrowser closed"}
					</span>
				</Label>
				<Switch
					id="alert-push"
					checked={thisBrowserOn}
					disabled={!supported || permission === "denied" || busy}
					onCheckedChange={(checked) => void handlePushToggle(checked)}
				/>
			</div>

			{(pushData?.subscriptions.length ?? 0) > 0 && (
				<div className="text-[11px] text-muted-foreground">
					Receiving on: {pushData!.subscriptions.map(s => s.user_agent ?? "Browser").join(", ")}
				</div>
			)}

			<div className="flex items-center justify-between gap-3 border-t border-border pt-3">
				<Label htmlFor="alert-email" className="flex flex-col gap-0.5 cursor-pointer flex-1">
					<span className="text-sm font-semibold">Also send email</span>
					<span className="text-xs text-muted-foreground font-normal leading-snug">
						Off by default — alerts can be frequent during severe weather
					</span>
				</Label>
				<Switch
					id="alert-email"
					checked={emailOn}
					onCheckedChange={(checked) => void handleEmailToggle(checked)}
				/>
			</div>

			{noChannel && (
				<p className="text-xs text-amber-500 leading-snug">
					Your location alerts have no way to reach you: enable browser
					notifications or email above.
				</p>
			)}
		</div>
	)
}

function LocationAlertRow({ rule }: { rule: PlacemarkAlertRuleRecord }) {
	const placemarkName = useAppSelector(state =>
		state.placemarks.placemarks[rule.placemark_id]?.name ?? "Deleted place")
	const [putRule] = usePutPlacemarkAlertMutation()
	const [deleteRule, { isLoading: deleting }] = useDeletePlacemarkAlertMutation()
	const { toast } = useToast()

	const update = async (changes: { enabled?: boolean; min_severity?: AlertRuleSeverity }) => {
		try {
			await putRule({
				placemarkId: rule.placemark_id,
				enabled: changes.enabled ?? rule.enabled,
				min_severity: changes.min_severity ?? rule.min_severity,
				event_filter: rule.event_filter,
			}).unwrap()
		} catch (error) {
			console.error("Failed to update alert rule:", error)
			toast({
				title: "Update Failed",
				variant: "destructive",
				description: "Unable to save this alert rule. Please try again.",
			})
		}
	}

	return (
		<div className="rounded-lg border border-border bg-background p-3">
			<div className="flex items-center gap-3">
				<MapPin className="h-4 w-4 shrink-0 text-teal-500" />
				<div className="flex-1 min-w-0">
					<div className="text-sm font-semibold truncate">{placemarkName}</div>
					{rule.last_notified_at && (
						<div className="text-[11px] text-muted-foreground">
							Last alert {new Date(rule.last_notified_at).toLocaleDateString()}
						</div>
					)}
				</div>
				<Select
					value={rule.min_severity}
					onValueChange={value => void update({ min_severity: value as AlertRuleSeverity })}
				>
					<SelectTrigger className="h-7 w-[140px] text-xs" aria-label="Minimum alert severity">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{cSeverityOptions.map(option => (
							<SelectItem key={option.value} value={option.value} className="text-xs">
								{option.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Switch
					checked={rule.enabled}
					onCheckedChange={checked => void update({ enabled: checked })}
					aria-label={`Toggle alerts for ${placemarkName}`}
				/>
				<button
					type="button"
					onClick={() => void deleteRule(rule.placemark_id)}
					disabled={deleting}
					aria-label={`Delete alert rule for ${placemarkName}`}
					className="p-1 rounded text-muted-foreground hover:text-destructive transition-colors disabled:opacity-50"
				>
					<Trash2 className="h-3.5 w-3.5" />
				</button>
			</div>
		</div>
	)
}
